import React, {
  ReactElement,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import config from '../../../config';
import { optimizeImageUrl } from '../../modules/imageLoader';

import Meta from '../../components/Meta/v2';
import { VerticalHeroV2 } from './VerticalHeroV2';
import { StatsV2 } from './StatsV2';
import { SuppliersV2 } from './SuppliersV2';
import { VerticalFAQ } from './VerticalFAQ';
import { Layout } from '../layout';
import { EventsPageProps } from '../../types';
import { useListingSchemas } from '../../hooks/useListingSchemas';
import {
  generateBreadcrumbSchema,
  generateBreadcrumbsFromPageData,
  mapSupplierToSchemaItem,
} from '../../utils/schema-org';
import { BookingBenefits as StyledBookingBenefits } from '../../components/BookingBenefits';
import { hasItemsInArray } from '../../utils/array';

// Dynamic imports for below-fold components to reduce blocking time
const LocationLinksV2 = dynamic(
  () =>
    import('./LocationLinksV2').then(mod => ({
      default: mod.LocationLinksV2,
    })),
  {
    ssr: false, // Client-side only - reduces main-thread work
    loading: () => <div>Loading location links...</div>,
  }
);

const VerticalStickyCTA = dynamic(
  () =>
    import('./VerticalStickyCTA/VerticalStickyCTA').then(mod => ({
      default: mod.VerticalStickyCTA,
    })),
  {
    ssr: false, // Interactive component - no need for SSR
    loading: () => <div>Loading...</div>,
  }
);
const EventTypes = dynamic(
  () =>
    import('./EventTypes').then(mod => ({
      default: mod.EventTypes,
    })),
  {
    ssr: false, // Below-fold content - client-side only
    loading: () => <div>Loading event types...</div>,
  }
);
const VerticalLinks = dynamic(
  () =>
    import('./VerticalLinksV2').then(mod => ({
      default: mod.VerticalLinksV2,
    })),
  {
    ssr: false, // Navigation links - client-side only
    loading: () => <div>Loading vertical links...</div>,
  }
);

const RelatedArticles = dynamic(
  () =>
    import('../../components/RelatedArticles/RelatedArticles').then(mod => ({
      default: mod.RelatedArticles,
    })),
  {
    ssr: false, // Below-fold articles - no SEO impact needed
    loading: () => <div>Loading articles...</div>,
  }
);

// Lazy load RelatedArticles only when it comes into viewport
const LazyRelatedArticles = ({ articles }: { articles: any[] }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && isMountedRef.current) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' } // Start loading 100px before it comes into view
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      isMountedRef.current = false;
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={ref}>
      {isVisible ? (
        <RelatedArticles articles={articles} />
      ) : (
        <div style={{ height: '400px' }} /> // Placeholder to maintain layout
      )}
    </div>
  );
};

const Reviews = dynamic(
  () =>
    import('./Reviews').then(mod => ({
      default: mod.Reviews,
    })),
  {
    ssr: false, // Reviews can load client-side for better performance
    loading: () => <div>Loading reviews...</div>,
  }
);

const CategoryLinksV2 = dynamic(
  () =>
    import('./CategoryLinksV2/index').then(mod => ({
      default: mod.CategoryLinksV2,
    })),
  {
    ssr: false, // Category navigation - client-side only
    loading: () => <div>Loading category links...</div>,
  }
);

interface VerticalPageProps extends EventsPageProps {
  nearMeCity: string;
  isLoading: boolean;
}

function VerticalPageV2({
  pageProperties,
  reviews,
  suppliers,
  averageRating,
  bookingCount,
  quoteCount,
  nearMeCity,
  isLoading,
  blogArticles,
}: VerticalPageProps): ReactElement {
  const pathname = usePathname();
  const rawImageSrc = suppliers?.results?.[0]?.public?.images?.[0]?.url || '';
  const imageSrc = optimizeImageUrl({
    src: rawImageSrc,
    width: 720,
    quality: 75,
  });

  const { canonicalPath, pageContent, inLocation } = pageProperties;
  const { metaData, seoVertical } = pageContent;

  // Extract complex expressions for dependency tracking
  const breadcrumbs = pageContent?.breadcrumbs;
  const contentSeoVertical = pageContent?.seoVertical;
  const contentSeoTag = pageContent?.seoTag;
  const locationVariable = pageProperties?.locationVariable;
  const locationVariableName = pageProperties?.locationVariable?.name;
  const suppliersResults = suppliers?.results;
  const faqComponentContent = pageProperties?.pageContent?.faqComponentContent;

  // Replace {count} placeholders in meta data with actual supplier count
  const amountOfSuppliers = suppliers?.results?.length || 0;
  const updatedMetaData = {
    ...metaData,
    title: metaData.title.replaceAll(/{count}/gi, amountOfSuppliers.toString()),
    description: metaData.description.replaceAll(
      /{count}/gi,
      amountOfSuppliers.toString()
    ),
  };

  const reviewsMeta =
    averageRating?.rating && averageRating?.count
      ? {
          averageRating: averageRating.rating.toFixed(1),
          reviewCount: averageRating.count,
        }
      : {};

  // Generate breadcrumb schema for V2 pages
  const breadcrumbSchema = useMemo(() => {
    // V2 pages have breadcrumbs in pageContent
    if (breadcrumbs?.length) {
      // Add Home breadcrumb and fix URL formatting
      const breadcrumbList = [
        { name: 'Home', url: `${config.baseUri}` },
        ...breadcrumbs.map(crumb => {
          // Provide meaningful fallback names based on URL or position
          let fallbackName = 'Unnamed item';
          if (crumb.url) {
            // Try to derive name from URL
            const urlParts = crumb.url.split('/').filter(part => part);
            const lastPart = urlParts[urlParts.length - 1];
            if (lastPart) {
              fallbackName =
                lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
            }
          }

          return {
            name: crumb.text || fallbackName,
            url: `${config.baseUri}/listings/${crumb.url}`,
          };
        }),
      ];
      return generateBreadcrumbSchema(breadcrumbList);
    }

    // Fallback to generating from page data
    const breadcrumbData = {
      seoVertical: contentSeoVertical,
      location: locationVariable
        ? {
            name: locationVariable.name,
          }
        : null,
      seoTag: contentSeoTag,
    };

    const breadcrumbsFromData = generateBreadcrumbsFromPageData(breadcrumbData);
    return generateBreadcrumbSchema(breadcrumbsFromData);
  }, [breadcrumbs, contentSeoVertical, contentSeoTag, locationVariable]);

  // Generate supplier schemas for V2 pages
  const supplierSchemas = useMemo(() => {
    if (!suppliersResults) return [];

    const locationText = locationVariableName || nearMeCity || 'UK';

    return suppliersResults.map(supplier =>
      mapSupplierToSchemaItem(supplier, locationText)
    );
  }, [suppliersResults, locationVariableName, nearMeCity]);

  // Generate page title for ItemList schema
  const pageTitle = useMemo(() => {
    const seoVertical = contentSeoVertical || 'Catering';
    const locationText = locationVariableName || nearMeCity || 'UK';
    const seoTag = contentSeoTag;

    if (seoTag) {
      return `${seoTag} in ${locationText}`;
    }
    return `${seoVertical} in ${locationText}`;
  }, [contentSeoVertical, contentSeoTag, locationVariableName, nearMeCity]);

  // Convert reviews to schema format
  const reviewSchemas = useMemo(() => {
    if (!reviews || !Array.isArray(reviews)) return [];
    return reviews.map(review => ({
      author: review.author || 'Anonymous',
      reviewBody: review.quote || '',
      reviewRating: review.rating,
      itemReviewed: 'Togather',
    }));
  }, [reviews]);

  // Convert FAQs to schema format
  const faqSchemas = useMemo(() => {
    if (!faqComponentContent || !Array.isArray(faqComponentContent)) return [];
    return faqComponentContent.map(faq => ({
      question: faq.question,
      answer: faq.answer,
    }));
  }, [faqComponentContent]);

  // Convert blog articles to schema format
  const blogSchemas = useMemo(() => {
    if (!blogArticles || !Array.isArray(blogArticles)) return [];
    return blogArticles.map(article => ({
      title: article.data?.metaTitle || article.title || '',
      url: `${config.baseUri}/blog/${article.uid}`,
      description: article.data?.introtext || article.description || '',
      datePublished:
        article.data?.shown_publication_date ||
        article.first_publication_date ||
        '',
      author: article.data?.author || 'Togather',
      image: article.data?.metaImage?.url || article.image || '',
      category: article.tags?.[0]?.name || article.category || '',
    }));
  }, [blogArticles]);

  // Generate page URL for schema
  const pageUrl = useMemo(() => {
    if (canonicalPath) {
      // If canonicalPath starts with http/https, use as-is, otherwise prepend domain
      return canonicalPath.startsWith('http')
        ? canonicalPath
        : `${config.baseUri}${canonicalPath}`;
    }

    return `${config.baseUri}${pathname}`;
  }, [canonicalPath, pathname]);

  // Add schemas to the page
  useListingSchemas({
    breadcrumbSchema,
    suppliers: supplierSchemas,
    pageTitle,
    pageUrl,
    reviews: reviewSchemas,
    faqs: faqSchemas,
    blogArticles: blogSchemas,
  });

  return (
    <Layout>
      <Meta
        imageSrc={imageSrc}
        path={canonicalPath || ''}
        {...updatedMetaData}
        {...reviewsMeta}
      />

      <VerticalHeroV2 imageSrc={imageSrc} pageProperties={pageProperties} />

      {!!averageRating && !!bookingCount && !!quoteCount && (
        <StatsV2
          vertical={{ slug: seoVertical.toLowerCase() }}
          averageRating={averageRating}
          bookingCount={bookingCount}
          quoteCount={quoteCount}
          {...pageProperties}
        />
      )}

      <SuppliersV2
        suppliers={suppliers}
        nearMeCity={nearMeCity || ''}
        isLoading={isLoading}
        pageProperties={pageProperties}
      />

      <StyledBookingBenefits />

      {!!pageProperties.pageContent?.relatedSeoTags?.length && (
        <CategoryLinksV2 pageProperties={pageProperties} />
      )}

      {hasItemsInArray(reviews) && <Reviews reviews={reviews} />}

      {blogArticles?.length > 0 && (
        <LazyRelatedArticles articles={blogArticles} />
      )}

      <VerticalFAQ pageProperties={pageProperties} />

      {pageProperties.pageContent?.eventTypes?.some(
        eventType => eventType.path
      ) &&
        pageProperties.pageContent?.eventTypeLinks?.length > 0 && (
          <EventTypes pageProperties={pageProperties} />
        )}

      <LocationLinksV2 pageProperties={pageProperties} />

      <VerticalLinks pageProperties={pageProperties} />

      {!isLoading && (
        <VerticalStickyCTA
          suppliers={suppliers}
          verticalName={seoVertical}
          inLocation={inLocation}
        />
      )}
    </Layout>
  );
}

export default VerticalPageV2;
